// Medicines brought back to the pharmacy (docs/plans/phase1-dispensing.md §4.8, §6.5).
//
// A return is requested by one person and approved by another (domain rule 10). Only
// what was given from a batch, less earlier approved returns, can come back. Restockable
// quantity re-enters the ledger on approval; the rest is recorded as not reusable.
//
// TODO(clinical-verify): plan §18 C5, accepted as the default on 25 Sep 2026 pending the
// clinical safety lead: a returned medicine may go back on the shelf only when the
// pharmacist marks it so (sealed, original pack, in date) and a second person approves;
// controlled and expired items never go back on the shelf.

import type { Money } from '../money.ts';
import { allocateMoney, moneyFromCents } from '../money.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import type { CalendarDay } from '../time.ts';
import { isBatchUsableOn } from '../stock/batch.ts';

export const RETURN_REASONS = [
  'unused',
  'wrong_item_dispensed',
  'adverse_reaction',
  'prescription_changed',
  'other',
] as const;
export type ReturnReason = (typeof RETURN_REASONS)[number];

export const RETURN_CONDITIONS = ['restockable', 'not_restockable'] as const;
export type ReturnCondition = (typeof RETURN_CONDITIONS)[number];

export type ReturnErrorCode =
  | 'pharmacy.return_exceeds_given'
  | 'pharmacy.return_quantity_invalid'
  | 'pharmacy.return_note_required';

export const RETURN_ERRORS: Record<ReturnErrorCode, string> = {
  'pharmacy.return_exceeds_given':
    'That is more than was given from this batch and not yet returned',
  'pharmacy.return_quantity_invalid': 'Enter a whole number above zero',
  'pharmacy.return_note_required': 'Say why when the reason is other',
};

/** What can still come back from one batch of one line. */
export function returnableQuantity(input: {
  readonly given: number;
  readonly alreadyReturned: number;
}): number {
  return Math.max(0, input.given - input.alreadyReturned);
}

export interface CheckedReturnLine {
  readonly quantity: number;
  readonly condition: ReturnCondition;
  /** True when the requested condition was restockable but the rules forced otherwise. */
  readonly forcedNotRestockable: boolean;
}

/**
 * Checks one return line. An expired batch or a controlled item is always not restockable,
 * whatever was asked; the caller shows that it was changed.
 */
export function checkReturnLine(input: {
  readonly quantity: number;
  readonly given: number;
  readonly alreadyReturned: number;
  readonly condition: ReturnCondition;
  readonly isControlled: boolean;
  readonly batchExpiry: CalendarDay | null;
  readonly onDay: CalendarDay;
}): Result<CheckedReturnLine, ReturnErrorCode> {
  if (!Number.isSafeInteger(input.quantity) || input.quantity <= 0)
    return err('pharmacy.return_quantity_invalid');
  if (input.quantity > returnableQuantity(input)) return err('pharmacy.return_exceeds_given');
  const mustNotRestock = input.isControlled || !isBatchUsableOn(input.batchExpiry, input.onDay);
  const forced = mustNotRestock && input.condition === 'restockable';
  return ok({
    quantity: input.quantity,
    condition: mustNotRestock ? 'not_restockable' : input.condition,
    forcedNotRestockable: forced,
  });
}

export function checkReturnReason(
  reason: ReturnReason,
  note: string | null | undefined,
): Result<ReturnReason, ReturnErrorCode> {
  if (reason === 'other' && (note ?? '').trim() === '') return err('pharmacy.return_note_required');
  return ok(reason);
}

export type ChargeCorrection =
  | { readonly kind: 'none' }
  /** Cancel the pending charge and, if anything is kept, charge the kept quantity again. */
  | { readonly kind: 'recharge'; readonly keptQuantity: number }
  /** The charge is billed: credit the returned quantity through billing's credit note. */
  | { readonly kind: 'credit_note'; readonly amount: Money };

/**
 * What happens to the bill when quantity comes back (plan §6.5). A pending charge is
 * replaced by one for the kept quantity; a billed one is credited for the returned share
 * of what its invoice line asked of the patient (split so the shares add up to the line);
 * a cancelled one needs nothing.
 */
export function chargeCorrection(input: {
  readonly chargeStatus: 'pending' | 'billed' | 'cancelled';
  /** The patient's invoice line amount for a billed charge; null when not invoiced. */
  readonly lineAmountCents: bigint | null;
  readonly chargedQuantity: number;
  readonly returnedQuantity: number;
}): ChargeCorrection {
  if (input.returnedQuantity <= 0 || input.chargeStatus === 'cancelled') return { kind: 'none' };
  if (input.returnedQuantity > input.chargedQuantity)
    throw new RangeError('Cannot return more than was charged');
  if (input.chargeStatus === 'pending')
    return { kind: 'recharge', keptQuantity: input.chargedQuantity - input.returnedQuantity };
  if (input.lineAmountCents === null || input.lineAmountCents === 0n) return { kind: 'none' };
  const [returned] = allocateMoney(moneyFromCents(input.lineAmountCents), [
    BigInt(input.returnedQuantity),
    BigInt(input.chargedQuantity - input.returnedQuantity),
  ]);
  if (returned === undefined || returned.cents === 0n) return { kind: 'none' };
  return { kind: 'credit_note', amount: returned };
}
